import { useLayoutEffect, useRef } from 'react'

/* A frame longer than this (a long task, a throttled tab) advances the film
   by this much at most, so a resumed loop never jumps a beat. */
const MAX_STEP_MS = 64

/* The clock runs while a fifth of the film is on screen. */
const IN_VIEW = { threshold: 0.2 }

/**
 * Drives a looping film from one rAF loop, with no React state per frame.
 * The clock advances only while `playing`, while the element is on screen
 * and while the page is visible; stopped, the film holds its frame and later
 * resumes from it. `onFrame(q, cycle, el)` receives the position in the loop
 * (0..1) and the number of loops already completed; it must be referentially
 * stable (define it at module level).
 *
 * The first loop starts `startAt` (0..1) into the cycle, after `delay` ms of
 * play, so films side by side do not run in lockstep; `firstCycle` numbers
 * it (1 continues from a still of a finished cycle). The current frame is
 * drawn before paint whenever the effect (re)starts.
 *
 * @param {{ current: HTMLElement | null }} ref
 * @param {{
 *   enabled: boolean,
 *   playing: boolean,
 *   duration: number,
 *   startAt?: number,
 *   delay?: number,
 *   firstCycle?: number,
 *   onFrame: (q: number, cycle: number, el: HTMLElement) => void,
 * }} options
 */
export default function useFilmLoop(
  ref,
  { enabled, playing, duration, startAt = 0, delay = 0, firstCycle = 0, onFrame },
) {
  // Milliseconds of play so far; kept across pauses and re-renders.
  const played = useRef(0)

  useLayoutEffect(() => {
    const el = ref.current
    if (!enabled || !el) return undefined

    let frame = 0
    let last = 0
    let inView = false
    let visible = document.visibilityState !== 'hidden'

    const draw = () => {
      const t = startAt * duration + Math.max(0, played.current - delay)
      onFrame((t % duration) / duration, firstCycle + Math.floor(t / duration), el)
    }

    const tick = (now) => {
      frame = requestAnimationFrame(tick)
      if (last) played.current += Math.min(MAX_STEP_MS, now - last)
      last = now
      draw()
    }

    const sync = () => {
      const run = playing && inView && visible
      if (run && !frame) {
        last = 0
        frame = requestAnimationFrame(tick)
      } else if (!run && frame) {
        cancelAnimationFrame(frame)
        frame = 0
      }
    }

    const observer = new IntersectionObserver(([entry]) => {
      inView = entry.isIntersecting
      sync()
    }, IN_VIEW)
    const onVisibility = () => {
      visible = document.visibilityState !== 'hidden'
      sync()
    }

    draw()
    observer.observe(el)
    document.addEventListener('visibilitychange', onVisibility)

    return () => {
      observer.disconnect()
      document.removeEventListener('visibilitychange', onVisibility)
      if (frame) cancelAnimationFrame(frame)
    }
  }, [ref, enabled, playing, duration, startAt, delay, firstCycle, onFrame])
}
